import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * The last thing between a thrown render error and a white screen.
 *
 * React 19 unmounts the entire tree when a render throws and nothing catches
 * it. On this site that means the nav, the page AND the footer disappear — and
 * the footer is a compliance surface (§06's required line, §8C's persistent
 * 18+ messaging, §3.1's Gambling Help and Lifeline numbers), which is why a
 * blank page here is worse than an ugly one. The fallback below restates the
 * support numbers for exactly that reason.
 *
 * It is a class because there is still no hook equivalent — `componentDidCatch`
 * and `getDerivedStateFromError` have no function-component form.
 *
 * Deliberately NOT a retry button. A render that threw once throws again on the
 * same state, so "Try again" that re-renders the same tree is a button that
 * appears to do nothing. Reloading discards the state that caused it.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    /*
     * console.error, not a telemetry call. There is no error-reporting service
     * wired up yet, and inventing one here would be the wrong call to make
     * silently — user-facing errors can carry whatever the component was
     * rendering, and where that goes is a privacy decision, not a plumbing one.
     * Source maps ship (vite.config.ts), so this trace names real files.
     */
    console.error("[kev] render error", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "60vh",
          display: "grid",
          alignContent: "center",
          gap: 16,
          padding: "64px 24px",
          maxWidth: "60ch",
          margin: "0 auto",
        }}
      >
        <p
          style={{
            fontFamily: "var(--font-data)",
            fontSize: 12,
            letterSpacing: "var(--tracking-eyebrow)",
            textTransform: "uppercase",
            color: "var(--muted-small)",
          }}
        >
          Something broke
        </p>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 40, color: "var(--ice)" }}>
          That&rsquo;s on us
        </h1>
        <p style={{ color: "var(--body)" }}>
          This page hit an error we didn&rsquo;t plan for. Nothing you did caused it, and
          nothing you were looking at was a result you can rely on.
        </p>
        <p>
          <a href="/">Back to the front</a>
        </p>
        <p style={{ fontSize: 14, color: "var(--muted)" }}>
          Gambling Help Online{" "}
          <a href="tel:1800858858">1800 858 858</a>, free and confidential, 24/7. Lifeline{" "}
          <a href="tel:131114">13 11 14</a>.
        </p>
      </div>
    );
  }
}
